import React, { useState, useEffect, useRef } from 'react';
import styles from './CanvasList.module.css';
import Sidebar from '../Sidebar/Sidebar';
import { IoArrowForward, IoArrowBack, IoAdd, IoTrashOutline, IoChevronDown, IoChevronForward } from 'react-icons/io5';
import { MdEdit, MdDragIndicator } from 'react-icons/md';
import { api } from '../../services/api';
import { supabase } from '../../supabaseClient';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import historyManager from '../../services/historyManager';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TextField, Button, CircularProgress } from '@mui/material';

const DraggableItem = ({ item, index, isSelected, onSelect, onRename, onDelete, onAddSection }) => {
  return (
    <Draggable draggableId={String(item.id || `separator-${index}`)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`${item.type === 'separator' ? styles.separatorContainer : styles.canvasItemWrapper} ${snapshot.isDragging ? styles.dragging : ''}`}
        >
          {item.type === 'separator' ? (
            <>
              <div {...provided.dragHandleProps} className={styles.dragHandle}>
                <MdDragIndicator size={16} />
              </div>
              <div className={styles.separator}>
                {item.title || 'Separator'}
              </div>
              <div className={styles.separatorActions}>
                <button 
                  className={styles.actionButton}
                  onClick={() => onRename(item, index)}
                >
                  <MdEdit size={16} />
                </button>
                <button 
                  className={styles.actionButton}
                  onClick={() => onDelete(item)}
                >
                  <IoTrashOutline size={16} />
                </button>
              </div>
            </>
          ) : (
            <>
              <div 
                {...provided.dragHandleProps}
                className={`${styles.canvasItem} ${isSelected ? styles.active : ''}`}
                onClick={() => onSelect(item, index)}
              >
                <div className={styles.dragHandle}>
                  <MdDragIndicator size={18} />
                </div>
                <span className={styles.canvasTitle}>{item.title || `Canvas ${index + 1}`}</span>
                <div className={styles.canvasActions}>
                  <button 
                    className={styles.actionButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRename(item);
                    }}
                  >
                    <MdEdit size={18} />
                  </button>
                  <button 
                    className={styles.actionButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item);
                    }}
                  >
                    <IoTrashOutline size={18} />
                  </button>
                </div>
              </div>
              <button 
                className={styles.addButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddSection(index);
                }}
                title="Add Separator"
              >
                <IoAdd size={20} />
              </button>
            </>
          )}
        </div>
      )}
    </Draggable>
  );
};

const CanvasList = ({ isOpen, onClose, conversation, onCanvasSelect, activeCanvas, onCanvasListUpdate }) => {
  const [canvases, setCanvases] = React.useState([]);
  const [selectedCanvasIndex, setSelectedCanvasIndex] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = React.useState(false);
  const [isAddSectionDialogOpen, setIsAddSectionDialogOpen] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState('');
  const [addSectionIndex, setAddSectionIndex] = React.useState(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [isAddingSection, setIsAddingSection] = React.useState(false);
  const [isTipsExpanded, setIsTipsExpanded] = React.useState(false);

  // Track current conversation ID to prevent duplicate selections
  const lastConversationRef = React.useRef(null);

  // Refs for input fields
  const renameInputRef = useRef(null);
  const addSectionInputRef = useRef(null);

  // Update canvases when conversation changes
  React.useEffect(() => {
    console.log('CanvasList received conversation:', conversation);
    if (!conversation) {
      setCanvases([]);
      return;
    }
    
    // Prefer canvas_details over canvas_list as it contains more information
    const canvasList = conversation.canvas_details || conversation.canvas_list || [];
    
    if (canvasList.length > 0) {
      console.log('Setting canvases from conversation:', canvasList);
      setCanvases(canvasList);
      
      // Only set index if we have an active canvas
      if (activeCanvas) {
        const index = canvasList.findIndex(c => c.id === activeCanvas.id);
        if (index !== -1) {
          setSelectedCanvasIndex(index);
        }
      }
    } else {
      setCanvases([]);
    }
  }, [conversation, activeCanvas]);

  // Keep selected index in sync with active canvas
  React.useEffect(() => {
    if (activeCanvas && canvases.length > 0) {
      const index = canvases.findIndex(c => c.id === activeCanvas.id);
      if (index !== -1 && index !== selectedCanvasIndex) {
        console.log('Syncing selected index with active canvas:', {
          activeId: activeCanvas.id,
          newIndex: index
        });
        setSelectedCanvasIndex(index) ;
      }
    }
  }, [activeCanvas, canvases, selectedCanvasIndex]);

  // Create a simplified list for backend with only necessary info
  const createSimplifiedList = (items) => {
    return items.map((item, idx) => {
      if (item.type === 'separator') {
        return {
          type: 'separator',
          title: item.title,
          id: item.id,
          position: idx
        };
      } else {
        return {
          type: 'canvas',
          id: item.id,
          position: idx
        };
      }
    });
  };

  // Save the updated list order to backend
  const saveCanvasListOrder = async (updatedList) => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('User authentication required');
        return { success: false };
      }

      const simplifiedList = createSimplifiedList(updatedList);
      
      // Send the updated list to backend
      const response = await api.post('/canvas_list_update', {
        conversation_id: conversation.id,
        canvas_list: simplifiedList,
        user_id: user.id
      });
      
      return { success: response.data.status === 'success' };
    } catch (error) {
      console.error('Error saving canvas list order:', error);
      return { success: false };
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source } = result;

    // If dropped outside the list or at the same position
    if (!destination || (destination.index === source.index)) {
      return;
    }

    // Create a copy of the current canvases
    const newCanvases = Array.from(canvases);
    const [removed] = newCanvases.splice(source.index, 1);
    newCanvases.splice(destination.index, 0, removed);

    // Show loading state
    setIsLoading(true);

    try {
      // Save to backend
      const result = await saveCanvasListOrder(newCanvases);

      if (result.success) {
        // Update the local state with the new order
        // This will also notify the parent component via updateCanvases
        updateCanvases(newCanvases);
        
        // Update selected index based on the new position
        let newSelectedIndex = selectedCanvasIndex;
        if (selectedCanvasIndex === source.index) {
          newSelectedIndex = destination.index;
        } else if (selectedCanvasIndex > source.index && selectedCanvasIndex <= destination.index) {
          newSelectedIndex--;
        } else if (selectedCanvasIndex < source.index && selectedCanvasIndex >= destination.index) {
          newSelectedIndex++;
        }
        setSelectedCanvasIndex(newSelectedIndex);
      } else {
        throw new Error('Failed to save the new order');
      }
    } catch (error) {
      console.error('Error during drag operation:', error);
      alert('Failed to save the new order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCanvasSelect = (canvas, index) => {
    if (!canvas || canvas.type === 'separator') {
      console.log('Invalid canvas or separator, ignoring');
      return;
    }
    
    console.log('Selecting canvas:', { id: canvas.id, index });
    setSelectedCanvasIndex(index);
    onCanvasSelect?.(canvas);
  };

  const handleRenameSeparator = async (separator, index) => {
    setSelectedItem(separator);
    setSelectedItemIndex(index);
    setNewTitle(separator.title);
    setIsRenameDialogOpen(true);
  };

  const handleRename = async (canvas) => {
    setSelectedItem(canvas);
    setSelectedItemIndex(null); // Not needed for canvas
    setNewTitle(canvas.title);
    setIsRenameDialogOpen(true);
  };

  const handleRenameSubmit = async () => {
    if (!selectedItem || !newTitle.trim() || newTitle === selectedItem.title) {
      setIsRenameDialogOpen(false);
      setSelectedItem(null);
      setNewTitle('');
      return;
    }

    setIsRenaming(true);

    try {
      if (selectedItem.type === 'separator') {
        // Handle separator rename
        const updatedCanvases = [...canvases];
        updatedCanvases[selectedItemIndex] = {
          ...selectedItem,
          title: newTitle.trim()
        };

        // Send the updated list to backend
        const result = await saveCanvasListOrder(updatedCanvases);

        if (result.success) {
          // Update local state with the renamed separator
          updateCanvases(updatedCanvases);
        } else {
          throw new Error('Failed to rename Separator');
        }
      } else {
        // Handle canvas rename
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User authentication required');
        }

        // Send rename request to backend
        const response = await api.post('/canvas_rename', {
          canvas_id: selectedItem.id,
          current_title: selectedItem.title,
          new_title: newTitle.trim(),
          user_id: user.id,
          conversation_id: conversation.id
        });

        if (response.data.status === 'success') {
          // Update canvas list in parent component with full details
          const updatedCanvases = canvases.map(c => 
            c.id === selectedItem.id ? { ...c, title: newTitle.trim() } : c
          );
          
          // Important: First update local state
          setCanvases(updatedCanvases);
          
          // Then notify parent component with full canvas details instead of simplified list
          if (onCanvasListUpdate && conversation) {
            onCanvasListUpdate(updatedCanvases);
          }
        } else {
          throw new Error('Failed to rename canvas');
        }
      }
    } catch (error) {
      console.error('Error renaming item:', error);
    } finally {
      setIsRenaming(false);
      setIsRenameDialogOpen(false);
      setSelectedItem(null);
      setNewTitle('');
    }
  };

  const handleDeleteSeparator = async (separator) => {
    setSelectedItem(separator);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async (canvas) => {
    setSelectedItem(canvas);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    
    setIsDeleting(true);

    try {
      if (selectedItem.type === 'separator') {
        // Handle separator deletion
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User authentication required');
        }

        // Send delete request to backend
        const response = await api.post('/separator_delete', {
          separator_id: selectedItem.id,
          conversation_id: conversation.id,
          user_id: user.id
        });

        if (response.data.status === 'deleted') {
          // Update local state by removing the separator
          const updatedCanvases = canvases.filter(item => 
            !(item.type === 'separator' && item.id === selectedItem.id)
          );
          updateCanvases(updatedCanvases);
        } else {
          throw new Error('Failed to delete Separator');
        }
      } else {
        // Handle canvas deletion
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User authentication required');
        }

        // Send delete request to backend
        const response = await api.post('/canvas_delete', {
          canvas_id: selectedItem.id,
          canvas_title: selectedItem.title,
          conversation_id: conversation.id,
          user_id: user.id
        });

        if (response.data.status === 'success') {
          // Update local state by removing the canvas
          const currentIndex = canvases.findIndex(item => item.id === selectedItem.id);
          const updatedCanvases = canvases.filter(item => item.id !== selectedItem.id);
          updateCanvases(updatedCanvases);
          
          // Update selected index if needed
          if (currentIndex === selectedCanvasIndex) {
            // If the deleted canvas was selected, select the previous one or the first one
            const newIndex = Math.max(0, currentIndex - 1);
            setSelectedCanvasIndex(newIndex);
            
            // If there are canvases left, select the one at the new index
            if (updatedCanvases.length > 0) {
              const newCanvas = updatedCanvases[newIndex];
              if (newCanvas.type === 'canvas') {
                onCanvasSelect(newCanvas);
              } else {
                // If it's a separator, find the next canvas
                const nextCanvasIndex = updatedCanvases.findIndex(
                  (item, idx) => idx >= newIndex && item.type === 'canvas'
                );
                if (nextCanvasIndex !== -1) {
                  setSelectedCanvasIndex(nextCanvasIndex);
                  onCanvasSelect(updatedCanvases[nextCanvasIndex]);
                }
              }
            } else {
              // No canvases left
              onCanvasSelect(null);
            }
          }
        } else {
          throw new Error('Failed to delete canvas');
        }
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  const handleAddSectionClick = (index) => {
    setAddSectionIndex(index);
    setNewTitle('');
    setIsAddSectionDialogOpen(true);
  };

  const handleAddSectionSubmit = async () => {
    if (!newTitle.trim()) {
      setIsAddSectionDialogOpen(false);
      setAddSectionIndex(null);
      setNewTitle('');
      return;
    }

    setIsAddingSection(true);

    try {
      // Create a new separator object
      const newSeparator = {
        type: 'separator',
        id: `separator-${Date.now()}`,
        title: newTitle.trim(),
        position: addSectionIndex + 1
      };

      // Insert the separator at the specified index
      const updatedCanvases = [...canvases];
      updatedCanvases.splice(addSectionIndex + 1, 0, newSeparator);

      // Save to backend
      const result = await saveCanvasListOrder(updatedCanvases);

      if (result.success) {
        // Update local state with the new separator
        updateCanvases(updatedCanvases);
      } else {
        throw new Error('Failed to add Separator');
      }
    } catch (error) {
      console.error('Error adding section:', error);
    } finally {
      setIsAddingSection(false);
      setIsAddSectionDialogOpen(false);
      setAddSectionIndex(null);
      setNewTitle('');
    }
  };

  // Custom setter for canvases that also notifies the parent component
  const updateCanvases = (newCanvases) => {
    console.log('Updating canvases:', newCanvases);
    setCanvases(newCanvases);
    if (onCanvasListUpdate && conversation) {
      // For ordering operations, only send the simplified info - for content operations like rename, this has been modified to send full details directly
      const simplifiedList = createSimplifiedList(newCanvases);
      console.log('Sending simplified list to parent:', simplifiedList);
      onCanvasListUpdate(simplifiedList);
    }
  };

  const renderContent = () => {
    if (canvases.length === 0) {
      return (
        <div className={styles.emptyState}>
          <p>No canvases yet</p>
          <p className={styles.emptyStateSubtext}>
            Canvases will appear here as you interact
          </p>
        </div>
      );
    }

    return (
      <div className={styles.canvasListContent}>
        <div 
          className={styles.instructionLabel}
          onClick={() => setIsTipsExpanded(!isTipsExpanded)}
        >
          <div className={styles.instructionLabelHeader}>
            <span>Quick Tips</span>
            {isTipsExpanded ? <IoChevronDown size={16} /> : <IoChevronForward size={16} />}
          </div>
          <div className={`${styles.instructionLabelContent} ${isTipsExpanded ? styles.expanded : ''}`}>
            <ul>
              <li>Use ←/→ arrow keys to navigate between canvases</li>
              <li>Drag and drop items to reorder them</li>
            </ul>
          </div>
        </div>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}></div>
          </div>
        )}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="canvas-list">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={styles.canvasList}
              >
                {canvases.map((item, index) => (
                  <DraggableItem
                    key={item.id || `separator-${index}`}
                    item={item}
                    index={index}
                    isSelected={index === selectedCanvasIndex}
                    onSelect={handleCanvasSelect}
                    onRename={item.type === 'separator' ? handleRenameSeparator : handleRename}
                    onDelete={item.type === 'separator' ? handleDeleteSeparator : handleDelete}
                    onAddSection={handleAddSectionClick}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  };

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      title="Canvas List"
    >
      {renderContent()}

      {/* Rename Dialog */}
      <Dialog
        open={isRenameDialogOpen && selectedItem !== null}
        onClose={() => {
          if (!isRenaming) {
            setIsRenameDialogOpen(false);
            setSelectedItem(null);
            setNewTitle('');
          }
        }}
        TransitionProps={{ 
          onEntered: () => {
            if (renameInputRef.current) {
              renameInputRef.current.focus();
              renameInputRef.current.select();
            }
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            maxWidth: '450px',
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: 'var(--text-lg)', 
          fontWeight: 'var(--font-semibold)', 
          color: 'var(--text-primary)',
          pt: 3,
          pb: 1
        }}>
          {selectedItem?.type === 'separator' ? 'Rename Separator' : 'Rename Canvas'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ 
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-base)',
            mb: 2
          }}>
            Enter a new name:
          </DialogContentText>
          <TextField
            inputRef={renameInputRef}
            margin="dense"
            value={newTitle}
            onChange={(e) => {
              // Limit to 100 characters
              if (e.target.value.length <= 100) {
                setNewTitle(e.target.value);
              }
            }}
            fullWidth
            variant="outlined"
            disabled={isRenaming}
            inputProps={{ maxLength: 100 }}
            helperText={`${newTitle.length}/100 characters`}
            onKeyDown={(e) => e.key === 'Enter' && !isRenaming && handleRenameSubmit()}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--radius-sm)',
                '&:hover fieldset': {
                  borderColor: 'var(--primary-light)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--primary)',
                },
              },
              '& .MuiFormHelperText-root': {
                fontSize: 'var(--text-xs)',
                color: 'var(--text-secondary)',
                mt: 0.5
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button 
            onClick={() => {
              setIsRenameDialogOpen(false);
              setSelectedItem(null);
              setNewTitle('');
            }}
            disabled={isRenaming}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'var(--font-medium)',
              color: 'var(--text-secondary)'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRenameSubmit} 
            color="primary" 
            variant="contained"
            disabled={isRenaming || !newTitle.trim()}
            startIcon={isRenaming ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'var(--font-medium)',
              px: 2,
              minWidth: '90px',
              bgcolor: 'var(--primary)',
              '&:hover': {
                bgcolor: 'var(--primary-hover)'
              }
            }}
          >
            {isRenaming ? 'Saving' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen && selectedItem !== null}
        onClose={() => {
          if (!isDeleting) {
            setIsDeleteDialogOpen(false);
            setSelectedItem(null);
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            maxWidth: '450px',
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: 'var(--text-lg)', 
          fontWeight: 'var(--font-semibold)', 
          color: 'var(--text-primary)',
          pt: 3,
          pb: 1
        }}>
          {selectedItem?.type === 'separator' ? 'Delete Separator' : 'Delete Canvas'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ 
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-base)',
            mb: 1
          }}>
            {selectedItem?.type === 'separator' 
              ? 'Are you sure you want to delete this Separator?' 
              : `Are you sure you want to delete "${selectedItem?.title}"?`}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button 
            onClick={() => setIsDeleteDialogOpen(false)}
            disabled={isDeleting}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'var(--font-medium)',
              color: 'var(--text-secondary)'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'var(--font-medium)',
              px: 2,
              minWidth: '80px'
            }}
          >
            {isDeleting ? 'Deleting' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Section Dialog */}
      <Dialog
        open={isAddSectionDialogOpen}
        onClose={() => {
          if (!isAddingSection) {
            setIsAddSectionDialogOpen(false);
            setAddSectionIndex(null);
            setNewTitle('');
          }
        }}
        TransitionProps={{ 
          onEntered: () => {
            if (addSectionInputRef.current) {
              addSectionInputRef.current.focus();
            }
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            maxWidth: '450px',
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: 'var(--text-lg)', 
          fontWeight: 'var(--font-semibold)', 
          color: 'var(--text-primary)',
          pt: 3,
          pb: 1
        }}>
          Add Separator
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ 
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-base)',
            mb: 2
          }}>
            Enter a name for the new Separator:
          </DialogContentText>
          <TextField
            inputRef={addSectionInputRef}
            margin="dense"
            value={newTitle}
            onChange={(e) => {
              // Limit to 100 characters
              if (e.target.value.length <= 100) {
                setNewTitle(e.target.value);
              }
            }}
            fullWidth
            variant="outlined"
            disabled={isAddingSection}
            inputProps={{ maxLength: 100 }}
            helperText={`${newTitle.length}/100 characters`}
            onKeyDown={(e) => e.key === 'Enter' && !isAddingSection && handleAddSectionSubmit()}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--radius-sm)',
                '&:hover fieldset': {
                  borderColor: 'var(--primary-light)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--primary)',
                },
              },
              '& .MuiFormHelperText-root': {
                fontSize: 'var(--text-xs)',
                color: 'var(--text-secondary)',
                mt: 0.5
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button 
            onClick={() => {
              setIsAddSectionDialogOpen(false);
              setAddSectionIndex(null);
              setNewTitle('');
            }}
            disabled={isAddingSection}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'var(--font-medium)',
              color: 'var(--text-secondary)'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddSectionSubmit} 
            color="primary" 
            variant="contained"
            disabled={isAddingSection || !newTitle.trim()}
            startIcon={isAddingSection ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 'var(--font-medium)',
              px: 2,
              minWidth: '90px',
              bgcolor: 'var(--primary)',
              '&:hover': {
                bgcolor: 'var(--primary-hover)'
              }
            }}
          >
            {isAddingSection ? 'Adding' : 'Add Separator'}
          </Button>
        </DialogActions>
      </Dialog>
    </Sidebar>
  );
};

export default CanvasList;